import { ArrowRight, CheckCircle2, FileText, FolderPlus, MessageCircleQuestion, MonitorSmartphone, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";
import { StageBar } from "./StageBar";
import { useMe } from "@/api/hooks/auth";
import { useFiles } from "@/api/hooks/files";
import { useOrgs, type OrgOut } from "@/api/hooks/orgs";
import { useProjectsForOrgs, type ProjectOut } from "@/api/hooks/projects";
import { usePrototypeBatches } from "@/api/hooks/prototypes";
import { usePageCrumbs } from "@/app/layouts/shellContext";
import { EmptyState } from "@/components/EmptyState";
import { ErrorNotice } from "@/components/ErrorNotice";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { currentVersion, isRecentConfirmation, isRecentShare } from "@/features/prototypes/shares";
import { cn } from "@/lib/utils";

function ProjectCard({ project }: { project: ProjectOut }) {
  const { t } = useTranslation();
  const files = useFiles(project.organisation_slug, project.slug);
  const batches = usePrototypeBatches(project.organisation_slug, project.slug);
  // Only the newest batch's current version counts: an older confirmed version must not read as "confirmed".
  const newest = batches.data?.[0];
  const current = newest ? currentVersion(newest.versions) : undefined;
  const newLabel =
    newest && current && isRecentShare(current.created_at)
      ? t("prototype.label", { batch: newest.number, version: current.version })
      : undefined;
  const confirmedBatch = newest && current && isRecentConfirmation(current.confirmed_at) ? newest.number : undefined;
  const pending = project.pending_required_count;
  const base = `/${project.organisation_slug}/${project.slug}`;
  return (
    <article
      className={cn(
        "group relative flex flex-col gap-4 rounded-lg border bg-surface p-6 shadow-card transition-shadow duration-base hover:shadow-elevated focus-within:ring-2 focus-within:ring-focus",
        pending > 0 ? "border-brand" : "border-border",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg">
          <Link
            to={base}
            className="no-underline after:absolute after:inset-0 after:content-empty focus-visible:outline-none focus-visible:ring-0"
          >
            {project.name}
          </Link>
        </h3>
        <StatusPill status={project.status} />
      </div>
      <StageBar status={project.status} />
      <p className={pending > 0 ? "flex items-center gap-2 font-semibold text-heading" : "flex items-center gap-2 text-fg-muted"}>
        <MessageCircleQuestion aria-hidden="true" className="h-5 w-5 shrink-0" />
        {project.questionnaire_ready_to_submit
          ? t("project.readyToSubmit")
          : pending > 0
            ? t("project.questionsWaiting", { count: pending })
            : t("project.allAnswered")}
      </p>
      {newLabel ? (
        <p className="flex items-center gap-2 text-sm font-semibold text-heading">
          <MonitorSmartphone aria-hidden="true" className="h-4 w-4 shrink-0" />
          {t("prototype.homeNew", { label: newLabel })}
        </p>
      ) : null}
      {confirmedBatch !== undefined ? (
        <p className="flex items-center gap-2 text-sm font-semibold text-heading">
          <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0" />
          {t("prototype.homeConfirmed", { batch: confirmedBatch })}
        </p>
      ) : null}
      <p className="line-clamp-2 text-sm text-fg-muted">{project.latest_activity ?? t("project.noActivity")}</p>
      <div className="mt-auto flex flex-wrap items-center gap-3 border-t border-border pt-4">
        <span className="inline-flex items-center gap-2 text-sm text-fg-muted">
          <FileText aria-hidden="true" className="h-4 w-4" />
          {files.data ? t("project.filesShort", { count: files.data.length }) : t("common.loading")}
        </span>
        {pending > 0 ? (
          <Button asChild size="sm" className="relative z-raised ml-auto">
            <Link to={`${base}/questionnaire?resume=1`} className="no-underline">
              {t("project.continueQuestionnaire")}
            </Link>
          </Button>
        ) : (
          <ArrowRight
            aria-hidden="true"
            className="ml-auto h-5 w-5 text-fg-subtle transition-transform duration-base group-hover:translate-x-1 group-hover:text-heading"
          />
        )}
      </div>
    </article>
  );
}

function CreateButton({ org }: { org: string }) {
  const { t } = useTranslation();
  return (
    <Button asChild>
      <Link to={`/${org}/projects/new`} className="no-underline">
        <Plus aria-hidden="true" className="h-5 w-5" />
        {t("project.createProject")}
      </Link>
    </Button>
  );
}

/** Home: what needs my attention, per organisation. One-project users go straight to it. */
export function HomePage() {
  const { t } = useTranslation();
  const { org: orgParam } = useParams();
  const me = useMe();
  const orgs = useOrgs();
  const visible = (orgs.data ?? []).filter((o) => !orgParam || o.slug === orgParam);
  const results = useProjectsForOrgs(visible.map((o) => o.slug));
  usePageCrumbs(orgParam && visible[0] ? [{ label: visible[0].name }] : []);

  if (orgs.isPending) return <PageLoading />;
  if (orgs.isError) return <ErrorNotice error={orgs.error} />;

  const isStaff = Boolean(me.data?.is_staff);
  const roleIn = (o: OrgOut) => (isStaff ? "staff" : me.data?.memberships.find((m) => m.organisation_slug === o.slug)?.role);
  const canCreate = (o: OrgOut) => roleIn(o) === "owner" || roleIn(o) === "staff";
  const groups = visible.map((o, i) => ({ org: o, query: results[i] }));
  const loading = groups.some((g) => g.query?.isPending);
  const total = groups.reduce((n, g) => n + (g.query?.data?.length ?? 0), 0);
  const showHeadings = groups.length > 1;
  const single = groups.length === 1 ? groups[0] : undefined;

  if (!orgParam && !isStaff && single && !loading && single.query?.data?.length === 1) {
    const only = single.query.data[0]!;
    return <Navigate to={`/${only.organisation_slug}/${only.slug}`} replace />;
  }

  return (
    <div>
      <PageHeader
        title={t("project.homeTitle")}
        description={t("project.homeIntro")}
        actions={single && canCreate(single.org) && total > 0 ? <CreateButton org={single.org.slug} /> : null}
      />
      {loading && total === 0 ? <PageLoading /> : null}
      {!loading && total === 0 ? (
        <EmptyState
          icon={<FolderPlus className="h-6 w-6" />}
          title={t("project.emptyTitle")}
          description={
            isStaff ? t("project.staffEmptyBody") : single && canCreate(single.org) ? t("project.emptyOwnerBody") : t("project.emptyBody")
          }
          action={
            single && canCreate(single.org) ? (
              <Button asChild>
                <Link to={`/${single.org.slug}/projects/new`} className="no-underline">
                  {t("project.createFirst")}
                </Link>
              </Button>
            ) : isStaff ? (
              <Button asChild variant="secondary">
                <Link to="/admin">{t("project.openAdmin")}</Link>
              </Button>
            ) : null
          }
        />
      ) : null}
      <div className="flex flex-col gap-10">
        {groups.map(({ org, query }) =>
          query?.data && query.data.length > 0 ? (
            <section key={org.id} aria-labelledby={showHeadings ? `org-${org.id}` : undefined}>
              {showHeadings ? (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <h2 id={`org-${org.id}`} className="text-xl">
                    {org.name}
                  </h2>
                  {canCreate(org) ? (
                    <Button asChild variant="secondary" size="sm">
                      <Link to={`/${org.slug}/projects/new`} className="no-underline">
                        <Plus aria-hidden="true" className="h-4 w-4" />
                        {t("project.createProject")}
                      </Link>
                    </Button>
                  ) : null}
                </div>
              ) : null}
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {query.data.map((p) => (
                  <ProjectCard key={p.id} project={p} />
                ))}
              </div>
            </section>
          ) : query?.isError ? (
            <ErrorNotice key={org.id} error={query.error} />
          ) : null,
        )}
      </div>
    </div>
  );
}
